import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react'

export type UiLanguageCode = 'en' | 'zh' | 'vi' | 'ja' | 'ko' | 'fr' | 'es'
type NonEnglishLanguage = Exclude<UiLanguageCode, 'en'>

export const UI_LANGUAGE_OPTIONS: Array<{ code: UiLanguageCode; label: string; shortLabel: string }> = [
  { code: 'en', label: 'English', shortLabel: 'EN' },
  { code: 'zh', label: 'Chinese Mandarin', shortLabel: '中文' },
  { code: 'vi', label: 'Vietnamese', shortLabel: 'VI' },
  { code: 'ja', label: 'Japanese', shortLabel: '日本語' },
  { code: 'ko', label: 'Korean', shortLabel: '한국어' },
  { code: 'fr', label: 'French', shortLabel: 'FR' },
  { code: 'es', label: 'Spanish', shortLabel: 'ES' },
]

const UI_LANGUAGE_STORAGE_KEY = 'euca.ui.language'
const DEFAULT_UI_LANGUAGE: UiLanguageCode = 'en'
const HTML_LANG_BY_CODE: Record<UiLanguageCode, string> = {
  en: 'en',
  zh: 'zh-CN',
  vi: 'vi',
  ja: 'ja',
  ko: 'ko',
  fr: 'fr',
  es: 'es',
}

const EXPLICIT_TRANSLATIONS: Record<NonEnglishLanguage, Record<string, string>> = {
  zh: {
    'Select language': '选择语言',
    'About Us': '关于我们',
    'Log In': '登录',
    'Log in': '登录',
    'Tell us what you need': '告诉我们您的需求',
    'Register as a professional': '注册成为专业人士',
    'Created by Dr Lily Vu and Dr Dong Fang Zhao': '由 Dr Lily Vu 与 Dr Dong Fang Zhao 创建',
    'Founders of EUCA MD®': 'EUCA MD® 创始人',
    'Verified Professionals Worldwide': '全球认证专业人士',
    'Find verified professionals in Australia, New Zealand, the US, Canada, the UK, Ireland, Singapore and Hong Kong.':
      '在澳大利亚、新西兰、美国、加拿大、英国、爱尔兰、新加坡和香港寻找认证专业人士。',
    Search: '搜索',
    Requests: '请求',
    Dashboard: '控制台',
    Account: '账户',
    Calendar: '日历',
    Register: '注册',
    Home: '首页',
    Clients: '客户',
    Professionals: '专业人士',
    Appointments: '预约',
    'Current requests': '当前请求',
    'Past requests': '历史请求',
    'Open requests': '开放请求',
    Accepted: '已接受',
    'Awaiting response': '等待回复',
    Pending: '待处理',
    'Ready to chat': '可开始聊天',
    Message: '消息',
    Messages: '消息',
    'Leave review': '留下评价',
    'Open chat': '打开聊天',
    Payment: '付款',
    'Set up payments': '设置收款',
    Meeting: '会议',
    NotesAI: 'AI 笔记',
    'Incoming requests': '新请求',
    'Scheduled consults': '已安排咨询',
    'No appointments yet.': '暂无预约。',
    'Book as a client': '以客户身份预约',
    'Work as a provider': '以专业人士身份工作',
    'Continue as client': '继续以客户身份',
    'Continue as provider': '继续以专业人士身份',
    'Welcome to': '欢迎来到',
    'How do you want to start?': '您希望如何开始？',
    'You’re offline. Changes will retry when connection resumes.': '您当前离线。网络恢复后将自动重试。',
    'Loading app…': '正在加载应用…',
    'Checking session…': '正在验证会话…',
    Google: 'Google',
    reviews: '条评价',
    'For Professionals': '面向专业人士',
    'Find Verified Professionals': '查找认证专业人士',
    'Your appointments': '您的预约',
    'Your confirmed bookings': '您已确认的预约',
    'New request': '新请求',
    Privacy: '隐私',
    Terms: '条款',
  },
  vi: {
    'Select language': 'Chọn ngôn ngữ',
    'About Us': 'Về chúng tôi',
    'Log In': 'Đăng nhập',
    'Log in': 'Đăng nhập',
    'Tell us what you need': 'Hãy cho chúng tôi biết nhu cầu của bạn',
    'Register as a professional': 'Đăng ký làm chuyên gia',
    'Created by Dr Lily Vu and Dr Dong Fang Zhao': 'Được tạo bởi Dr Lily Vu và Dr Dong Fang Zhao',
    'Founders of EUCA MD®': 'Nhà sáng lập EUCA MD®',
    'Verified Professionals Worldwide': 'Chuyên gia đã xác minh trên toàn thế giới',
    'Find verified professionals in Australia, New Zealand, the US, Canada, the UK, Ireland, Singapore and Hong Kong.':
      'Tìm chuyên gia đã xác minh tại Úc, New Zealand, Mỹ, Canada, Anh, Ireland, Singapore và Hồng Kông.',
    Search: 'Tìm kiếm',
    Requests: 'Yêu cầu',
    Dashboard: 'Bảng điều khiển',
    Account: 'Tài khoản',
    Calendar: 'Lịch',
    Register: 'Đăng ký',
    Home: 'Trang chủ',
    Clients: 'Khách hàng',
    Professionals: 'Chuyên gia',
    Appointments: 'Lịch hẹn',
    'Current requests': 'Yêu cầu hiện tại',
    'Past requests': 'Yêu cầu trước đây',
    'Open requests': 'Yêu cầu đang mở',
    Accepted: 'Đã chấp nhận',
    'Awaiting response': 'Đang chờ phản hồi',
    Pending: 'Đang chờ',
    'Ready to chat': 'Sẵn sàng trò chuyện',
    Message: 'Nhắn tin',
    Messages: 'Tin nhắn',
    'Leave review': 'Để lại đánh giá',
    'Open chat': 'Mở chat',
    Payment: 'Thanh toán',
    'Set up payments': 'Thiết lập thanh toán',
    Meeting: 'Cuộc họp',
    NotesAI: 'Ghi chú AI',
    'Incoming requests': 'Yêu cầu mới',
    'Scheduled consults': 'Lịch tư vấn đã đặt',
    'No appointments yet.': 'Chưa có lịch hẹn.',
    'Book as a client': 'Đặt lịch với tư cách khách hàng',
    'Work as a provider': 'Làm việc với tư cách chuyên gia',
    'Continue as client': 'Tiếp tục với tư cách khách hàng',
    'Continue as provider': 'Tiếp tục với tư cách chuyên gia',
    'Welcome to': 'Chào mừng đến với',
    'How do you want to start?': 'Bạn muốn bắt đầu như thế nào?',
    'You’re offline. Changes will retry when connection resumes.': 'Bạn đang ngoại tuyến. Hệ thống sẽ thử lại khi có kết nối.',
    'Loading app…': 'Đang tải ứng dụng…',
    'Checking session…': 'Đang kiểm tra phiên…',
    Google: 'Google',
    reviews: 'đánh giá',
    'For Professionals': 'Dành cho chuyên gia',
    'Find Verified Professionals': 'Tìm chuyên gia đã xác minh',
    'Your appointments': 'Lịch hẹn của bạn',
    'Your confirmed bookings': 'Lịch đã xác nhận',
    'New request': 'Yêu cầu mới',
    Privacy: 'Quyền riêng tư',
    Terms: 'Điều khoản',
  },
  ja: {
    'Select language': '言語を選択',
    'About Us': '私たちについて',
    'Log In': 'ログイン',
    'Log in': 'ログイン',
    'Tell us what you need': '必要な内容をお聞かせください',
    'Register as a professional': '専門家として登録',
    'Created by Dr Lily Vu and Dr Dong Fang Zhao': 'Dr Lily Vu と Dr Dong Fang Zhao が設立',
    'Founders of EUCA MD®': 'EUCA MD® 創業者',
    'Verified Professionals Worldwide': '世界中の認証済み専門家',
    'Find verified professionals in Australia, New Zealand, the US, Canada, the UK, Ireland, Singapore and Hong Kong.':
      'オーストラリア、ニュージーランド、米国、カナダ、英国、アイルランド、シンガポール、香港の認証済み専門家を探せます。',
    Search: '検索',
    Requests: '依頼',
    Dashboard: 'ダッシュボード',
    Account: 'アカウント',
    Calendar: 'カレンダー',
    Register: '登録',
    Home: 'ホーム',
    Clients: 'クライアント',
    Professionals: '専門家',
    Appointments: '予約',
    'Current requests': '現在の依頼',
    'Past requests': '過去の依頼',
    'Open requests': '受付中の依頼',
    Accepted: '承認済み',
    'Awaiting response': '返信待ち',
    Pending: '保留中',
    'Ready to chat': 'チャット可能',
    Message: 'メッセージ',
    Messages: 'メッセージ',
    'Leave review': 'レビューを書く',
    'Open chat': 'チャットを開く',
    Payment: '支払い',
    'Set up payments': '支払い設定',
    Meeting: 'ミーティング',
    NotesAI: 'AIノート',
    'Incoming requests': '新しい依頼',
    'Scheduled consults': '予定済み相談',
    'No appointments yet.': '予約はまだありません。',
    'Book as a client': 'クライアントとして予約',
    'Work as a provider': '専門家として利用',
    'Continue as client': 'クライアントとして続行',
    'Continue as provider': '専門家として続行',
    'Welcome to': 'ようこそ',
    'How do you want to start?': 'どのように始めますか？',
    'You’re offline. Changes will retry when connection resumes.': 'オフラインです。接続が戻ると再試行します。',
    'Loading app…': 'アプリを読み込み中…',
    'Checking session…': 'セッション確認中…',
    Google: 'Google',
    reviews: '件のレビュー',
    'For Professionals': '専門家向け',
    'Find Verified Professionals': '認証済み専門家を探す',
    'Your appointments': 'あなたの予約',
    'Your confirmed bookings': '確定した予約',
    'New request': '新しい依頼',
    Privacy: 'プライバシー',
    Terms: '利用規約',
  },
  ko: {
    'Select language': '언어 선택',
    'About Us': '회사 소개',
    'Log In': '로그인',
    'Log in': '로그인',
    'Tell us what you need': '필요한 내용을 알려주세요',
    'Register as a professional': '전문가로 등록',
    'Created by Dr Lily Vu and Dr Dong Fang Zhao': 'Dr Lily Vu와 Dr Dong Fang Zhao가 설립',
    'Founders of EUCA MD®': 'EUCA MD® 공동 창립자',
    'Verified Professionals Worldwide': '전 세계 검증된 전문가',
    'Find verified professionals in Australia, New Zealand, the US, Canada, the UK, Ireland, Singapore and Hong Kong.':
      '호주, 뉴질랜드, 미국, 캐나다, 영국, 아일랜드, 싱가포르, 홍콩에서 검증된 전문가를 찾아보세요.',
    Search: '검색',
    Requests: '요청',
    Dashboard: '대시보드',
    Account: '계정',
    Calendar: '캘린더',
    Register: '등록',
    Home: '홈',
    Clients: '고객',
    Professionals: '전문가',
    Appointments: '예약',
    'Current requests': '현재 요청',
    'Past requests': '지난 요청',
    'Open requests': '진행 중 요청',
    Accepted: '수락됨',
    'Awaiting response': '응답 대기',
    Pending: '보류',
    'Ready to chat': '채팅 가능',
    Message: '메시지',
    Messages: '메시지',
    'Leave review': '리뷰 남기기',
    'Open chat': '채팅 열기',
    Payment: '결제',
    'Set up payments': '결제 설정',
    Meeting: '미팅',
    NotesAI: 'AI 노트',
    'Incoming requests': '신규 요청',
    'Scheduled consults': '예약된 상담',
    'No appointments yet.': '아직 예약이 없습니다.',
    'Book as a client': '고객으로 예약',
    'Work as a provider': '전문가로 이용',
    'Continue as client': '고객으로 계속',
    'Continue as provider': '전문가로 계속',
    'Welcome to': '환영합니다',
    'How do you want to start?': '어떻게 시작하시겠어요?',
    'You’re offline. Changes will retry when connection resumes.': '오프라인 상태입니다. 연결이 복구되면 다시 시도합니다.',
    'Loading app…': '앱 로딩 중…',
    'Checking session…': '세션 확인 중…',
    Google: 'Google',
    reviews: '리뷰',
    'For Professionals': '전문가용',
    'Find Verified Professionals': '검증된 전문가 찾기',
    'Your appointments': '내 예약',
    'Your confirmed bookings': '확정된 예약',
    'New request': '신규 요청',
    Privacy: '개인정보',
    Terms: '약관',
  },
  fr: {
    'Select language': 'Choisir la langue',
    'About Us': 'À propos',
    'Log In': 'Se connecter',
    'Log in': 'Se connecter',
    'Tell us what you need': 'Dites-nous ce dont vous avez besoin',
    'Register as a professional': 'S’inscrire comme professionnel',
    'Created by Dr Lily Vu and Dr Dong Fang Zhao': 'Créé par Dr Lily Vu et Dr Dong Fang Zhao',
    'Founders of EUCA MD®': 'Fondateurs de EUCA MD®',
    'Verified Professionals Worldwide': 'Professionnels vérifiés dans le monde',
    'Find verified professionals in Australia, New Zealand, the US, Canada, the UK, Ireland, Singapore and Hong Kong.':
      'Trouvez des professionnels vérifiés en Australie, en Nouvelle-Zélande, aux États-Unis, au Canada, au Royaume-Uni, en Irlande, à Singapour et à Hong Kong.',
    Search: 'Recherche',
    Requests: 'Demandes',
    Dashboard: 'Tableau de bord',
    Account: 'Compte',
    Calendar: 'Calendrier',
    Register: 'Inscription',
    Home: 'Accueil',
    Clients: 'Clients',
    Professionals: 'Professionnels',
    Appointments: 'Rendez-vous',
    'Current requests': 'Demandes en cours',
    'Past requests': 'Demandes passées',
    'Open requests': 'Demandes ouvertes',
    Accepted: 'Accepté',
    'Awaiting response': 'En attente de réponse',
    Pending: 'En attente',
    'Ready to chat': 'Prêt à discuter',
    Message: 'Message',
    Messages: 'Messages',
    'Leave review': 'Laisser un avis',
    'Open chat': 'Ouvrir le chat',
    Payment: 'Paiement',
    'Set up payments': 'Configurer les paiements',
    Meeting: 'Réunion',
    NotesAI: 'Notes IA',
    'Incoming requests': 'Nouvelles demandes',
    'Scheduled consults': 'Consultations planifiées',
    'No appointments yet.': 'Aucun rendez-vous pour le moment.',
    'Book as a client': 'Réserver en tant que client',
    'Work as a provider': 'Travailler comme professionnel',
    'Continue as client': 'Continuer comme client',
    'Continue as provider': 'Continuer comme professionnel',
    'Welcome to': 'Bienvenue sur',
    'How do you want to start?': 'Comment souhaitez-vous commencer ?',
    'You’re offline. Changes will retry when connection resumes.': 'Vous êtes hors ligne. Les actions reprendront à la reconnexion.',
    'Loading app…': 'Chargement de l’application…',
    'Checking session…': 'Vérification de la session…',
    Google: 'Google',
    reviews: 'avis',
    'For Professionals': 'Pour les professionnels',
    'Find Verified Professionals': 'Trouver des professionnels vérifiés',
    'Your appointments': 'Vos rendez-vous',
    'Your confirmed bookings': 'Vos réservations confirmées',
    'New request': 'Nouvelle demande',
    Privacy: 'Confidentialité',
    Terms: 'Conditions',
  },
  es: {
    'Select language': 'Seleccionar idioma',
    'About Us': 'Sobre nosotros',
    'Log In': 'Iniciar sesión',
    'Log in': 'Iniciar sesión',
    'Tell us what you need': 'Cuéntanos qué necesitas',
    'Register as a professional': 'Regístrate como profesional',
    'Created by Dr Lily Vu and Dr Dong Fang Zhao': 'Creado por Dr Lily Vu y Dr Dong Fang Zhao',
    'Founders of EUCA MD®': 'Fundadores de EUCA MD®',
    'Verified Professionals Worldwide': 'Profesionales verificados en todo el mundo',
    'Find verified professionals in Australia, New Zealand, the US, Canada, the UK, Ireland, Singapore and Hong Kong.':
      'Encuentra profesionales verificados en Australia, Nueva Zelanda, Estados Unidos, Canadá, Reino Unido, Irlanda, Singapur y Hong Kong.',
    Search: 'Buscar',
    Requests: 'Solicitudes',
    Dashboard: 'Panel',
    Account: 'Cuenta',
    Calendar: 'Calendario',
    Register: 'Registro',
    Home: 'Inicio',
    Clients: 'Clientes',
    Professionals: 'Profesionales',
    Appointments: 'Citas',
    'Current requests': 'Solicitudes actuales',
    'Past requests': 'Solicitudes pasadas',
    'Open requests': 'Solicitudes abiertas',
    Accepted: 'Aceptado',
    'Awaiting response': 'Esperando respuesta',
    Pending: 'Pendiente',
    'Ready to chat': 'Listo para chatear',
    Message: 'Mensaje',
    Messages: 'Mensajes',
    'Leave review': 'Dejar reseña',
    'Open chat': 'Abrir chat',
    Payment: 'Pago',
    'Set up payments': 'Configurar pagos',
    Meeting: 'Reunión',
    NotesAI: 'Notas IA',
    'Incoming requests': 'Nuevas solicitudes',
    'Scheduled consults': 'Consultas programadas',
    'No appointments yet.': 'Aún no hay citas.',
    'Book as a client': 'Reservar como cliente',
    'Work as a provider': 'Trabajar como profesional',
    'Continue as client': 'Continuar como cliente',
    'Continue as provider': 'Continuar como profesional',
    'Welcome to': 'Bienvenido a',
    'How do you want to start?': '¿Cómo quieres empezar?',
    'You’re offline. Changes will retry when connection resumes.': 'Estás sin conexión. Se reintentará cuando vuelva la conexión.',
    'Loading app…': 'Cargando la app…',
    'Checking session…': 'Comprobando sesión…',
    Google: 'Google',
    reviews: 'reseñas',
    'For Professionals': 'Para profesionales',
    'Find Verified Professionals': 'Encontrar profesionales verificados',
    'Your appointments': 'Tus citas',
    'Your confirmed bookings': 'Tus reservas confirmadas',
    'New request': 'Nueva solicitud',
    Privacy: 'Privacidad',
    Terms: 'Términos',
  },
}

const TOKEN_TRANSLATIONS: Record<NonEnglishLanguage, Record<string, string>> = {
  zh: {
    Dashboard: '控制台',
    Request: '请求',
    Requests: '请求',
    Appointment: '预约',
    Appointments: '预约',
    Account: '账户',
    Calendar: '日历',
    Search: '搜索',
    Message: '消息',
    Messages: '消息',
    Client: '客户',
    Clients: '客户',
    Professional: '专业人士',
    Professionals: '专业人士',
    Register: '注册',
    Login: '登录',
    Resources: '资源',
    Company: '公司',
    Product: '产品',
    Products: '产品',
    Knowledge: '知识',
    Base: '库',
    Help: '帮助',
    Centre: '中心',
    Center: '中心',
    Financial: '财务',
    Services: '服务',
    Legal: '法律',
    Governance: '治理',
    Compliance: '合规',
    Migration: '移民',
    Visa: '签证',
    Education: '教育',
    Tutoring: '辅导',
    Property: '房产',
    Design: '设计',
    Building: '建筑',
    Allied: '联合',
    Health: '健康',
    Advice: '建议',
    Meeting: '会议',
    Payment: '付款',
    Accepted: '已接受',
    Pending: '待处理',
  },
  vi: {
    Dashboard: 'Bảng điều khiển',
    Request: 'Yêu cầu',
    Requests: 'Yêu cầu',
    Appointment: 'Lịch hẹn',
    Appointments: 'Lịch hẹn',
    Account: 'Tài khoản',
    Calendar: 'Lịch',
    Search: 'Tìm kiếm',
    Message: 'Nhắn tin',
    Messages: 'Tin nhắn',
    Client: 'Khách hàng',
    Clients: 'Khách hàng',
    Professional: 'Chuyên gia',
    Professionals: 'Chuyên gia',
    Register: 'Đăng ký',
    Login: 'Đăng nhập',
    Resources: 'Tài nguyên',
    Company: 'Công ty',
    Product: 'Sản phẩm',
    Products: 'Sản phẩm',
    Knowledge: 'Kiến thức',
    Base: 'Kho',
    Help: 'Trợ giúp',
    Centre: 'Trung tâm',
    Center: 'Trung tâm',
    Financial: 'Tài chính',
    Services: 'Dịch vụ',
    Legal: 'Pháp lý',
    Governance: 'Quản trị',
    Compliance: 'Tuân thủ',
    Migration: 'Di trú',
    Visa: 'Visa',
    Education: 'Giáo dục',
    Tutoring: 'Gia sư',
    Property: 'Bất động sản',
    Design: 'Thiết kế',
    Building: 'Xây dựng',
    Allied: 'Liên ngành',
    Health: 'Y tế',
    Advice: 'Tư vấn',
    Meeting: 'Cuộc họp',
    Payment: 'Thanh toán',
    Accepted: 'Đã chấp nhận',
    Pending: 'Đang chờ',
  },
  ja: {
    Dashboard: 'ダッシュボード',
    Request: '依頼',
    Requests: '依頼',
    Appointment: '予約',
    Appointments: '予約',
    Account: 'アカウント',
    Calendar: 'カレンダー',
    Search: '検索',
    Message: 'メッセージ',
    Messages: 'メッセージ',
    Client: 'クライアント',
    Clients: 'クライアント',
    Professional: '専門家',
    Professionals: '専門家',
    Register: '登録',
    Login: 'ログイン',
    Resources: 'リソース',
    Company: '会社',
    Product: '製品',
    Products: '製品',
    Knowledge: '知識',
    Base: 'ベース',
    Help: 'ヘルプ',
    Centre: 'センター',
    Center: 'センター',
    Financial: '金融',
    Services: 'サービス',
    Legal: '法務',
    Governance: 'ガバナンス',
    Compliance: 'コンプライアンス',
    Migration: '移民',
    Visa: 'ビザ',
    Education: '教育',
    Tutoring: '個別指導',
    Property: '不動産',
    Design: '設計',
    Building: '建築',
    Allied: 'コメディカル',
    Health: '医療',
    Advice: 'アドバイス',
    Meeting: 'ミーティング',
    Payment: '支払い',
    Accepted: '承認済み',
    Pending: '保留中',
  },
  ko: {
    Dashboard: '대시보드',
    Request: '요청',
    Requests: '요청',
    Appointment: '예약',
    Appointments: '예약',
    Account: '계정',
    Calendar: '캘린더',
    Search: '검색',
    Message: '메시지',
    Messages: '메시지',
    Client: '고객',
    Clients: '고객',
    Professional: '전문가',
    Professionals: '전문가',
    Register: '등록',
    Login: '로그인',
    Resources: '리소스',
    Company: '회사',
    Product: '제품',
    Products: '제품',
    Knowledge: '지식',
    Base: '베이스',
    Help: '도움말',
    Centre: '센터',
    Center: '센터',
    Financial: '재무',
    Services: '서비스',
    Legal: '법률',
    Governance: '거버넌스',
    Compliance: '컴플라이언스',
    Migration: '이민',
    Visa: '비자',
    Education: '교육',
    Tutoring: '튜터링',
    Property: '부동산',
    Design: '디자인',
    Building: '건축',
    Allied: '보건',
    Health: '의료',
    Advice: '상담',
    Meeting: '미팅',
    Payment: '결제',
    Accepted: '수락됨',
    Pending: '보류',
  },
  fr: {
    Dashboard: 'Tableau de bord',
    Request: 'Demande',
    Requests: 'Demandes',
    Appointment: 'Rendez-vous',
    Appointments: 'Rendez-vous',
    Account: 'Compte',
    Calendar: 'Calendrier',
    Search: 'Recherche',
    Message: 'Message',
    Messages: 'Messages',
    Client: 'Client',
    Clients: 'Clients',
    Professional: 'Professionnel',
    Professionals: 'Professionnels',
    Register: 'Inscription',
    Login: 'Connexion',
    Resources: 'Ressources',
    Company: 'Entreprise',
    Product: 'Produit',
    Products: 'Produits',
    Knowledge: 'Connaissances',
    Base: 'Base',
    Help: 'Aide',
    Centre: 'Centre',
    Center: 'Centre',
    Financial: 'Finance',
    Services: 'Services',
    Legal: 'Juridique',
    Governance: 'Gouvernance',
    Compliance: 'Conformité',
    Migration: 'Migration',
    Visa: 'Visa',
    Education: 'Éducation',
    Tutoring: 'Tutorat',
    Property: 'Immobilier',
    Design: 'Design',
    Building: 'Construction',
    Allied: 'Paramédical',
    Health: 'Santé',
    Advice: 'Conseil',
    Meeting: 'Réunion',
    Payment: 'Paiement',
    Accepted: 'Accepté',
    Pending: 'En attente',
  },
  es: {
    Dashboard: 'Panel',
    Request: 'Solicitud',
    Requests: 'Solicitudes',
    Appointment: 'Cita',
    Appointments: 'Citas',
    Account: 'Cuenta',
    Calendar: 'Calendario',
    Search: 'Buscar',
    Message: 'Mensaje',
    Messages: 'Mensajes',
    Client: 'Cliente',
    Clients: 'Clientes',
    Professional: 'Profesional',
    Professionals: 'Profesionales',
    Register: 'Registro',
    Login: 'Iniciar sesión',
    Resources: 'Recursos',
    Company: 'Empresa',
    Product: 'Producto',
    Products: 'Productos',
    Knowledge: 'Conocimiento',
    Base: 'Base',
    Help: 'Ayuda',
    Centre: 'Centro',
    Center: 'Centro',
    Financial: 'Finanzas',
    Services: 'Servicios',
    Legal: 'Legal',
    Governance: 'Gobernanza',
    Compliance: 'Cumplimiento',
    Migration: 'Migración',
    Visa: 'Visa',
    Education: 'Educación',
    Tutoring: 'Tutoría',
    Property: 'Propiedad',
    Design: 'Diseño',
    Building: 'Construcción',
    Allied: 'Aliada',
    Health: 'Salud',
    Advice: 'Asesoría',
    Meeting: 'Reunión',
    Payment: 'Pago',
    Accepted: 'Aceptado',
    Pending: 'Pendiente',
  },
}

const EXTRA_EXPLICIT_TRANSLATIONS: Record<NonEnglishLanguage, Record<string, string>> = {
  zh: {
    'Review your submitted request details and response counts.': '查看你已提交的请求详情和回复统计。',
    'Response summary': '回复概览',
    'Totals across your current and past requests.': '当前与历史请求的汇总。',
    'Active requests with open responses.': '仍在等待回复的进行中请求。',
    'Completed, expired, cancelled, and closed requests.': '已完成、已过期、已取消和已关闭的请求。',
    'Accepted professionals': '已接受的专业人士',
    'Open chat and continue your request.': '打开聊天并继续你的请求。',
    'No accepted professionals yet.': '目前还没有专业人士接受。',
    'Manage bookings, notes, payments, and message clients from one place.': '在一个页面管理预约、笔记、收款，并与客户沟通。',
    'No clients yet.': '暂无客户。',
    'Once you receive a request, clients will appear here.': '收到请求后，客户会显示在这里。',
    'Ready to notify the 3 most suitable professionals?': '准备通知最匹配的 3 位专业人士吗？',
    'We send your request to the top matches only. All conversations stay in EUCA Match chat after login.':
      '我们只会把请求发送给最匹配的人选。登录后，所有沟通都在 EUCA Match 聊天中进行。',
    'Top matches from verified professionals.': '来自认证专业人士的最佳匹配。',
    'No professionals found yet in this category.': '该类别暂未找到专业人士。',
    'No professionals match your filters.': '没有专业人士符合你的筛选条件。',
    'Identity & Compliance': '身份与合规',
    'Documents & Verification': '文件与认证',
    'Identity verification (choose ONE option)': '身份验证（请选择一种方式）',
    'Use the same email and phone for clients': '对客户使用相同的邮箱和电话',
    'Finish your registration to start receiving client requests.': '完成注册后即可开始接收客户请求。',
    'Legal, Governance & Compliance': '法律、治理与合规',
    'Financial Advisory': '财务咨询',
    'Migration & Visa': '移民与签证',
    'Education & Tutoring': '教育与辅导',
    'Property, Design & Building': '房产、设计与建筑',
    'Tech, Creative & Media': '科技、创意与媒体',
    'Medical Doctors': '医疗医生',
    'Allied Health': '联合健康',
    'Client-facing contact': '面向客户的联系方式',
    'Profile & pricing': '资料与定价',
    'Pricing notes (optional)': '价格说明（可选）',
    'Open Client Dashboard': '打开客户端控制台',
    'Open dashboard': '打开控制台',
    'Message client': '联系客户',
    'Message unavailable': '暂不可发送消息',
    'No pending requests.': '暂无待处理请求。',
    'Try another filter or check back soon.': '请尝试其他筛选条件，或稍后再查看。',
    'No requests yet.': '暂无请求。',
    'No appointments.': '暂无预约。',
    'No upcoming appointments yet.': '暂无即将到来的预约。',
    'No upcoming slots.': '暂无可用时段。',
    'Secure payment': '安全支付',
    'Review selection': '确认选择',
    'Pick a slot': '选择时段',
    'Ready to contact professionals?': '准备联系专业人士了吗？',
    'Refine your search': '优化你的搜索',
    'Primary recommendation': '主要推荐',
    'You may also need': '你可能还需要',
    'If relevant, explore this category too.': '如有需要，也可查看此类别。',
    'AI Search': 'AI 搜索',
    'What do you need help with?': '你需要哪方面帮助？',
    'Search suggestions': '搜索建议',
    'See more professionals': '查看更多专业人士',
    'Arrange details directly with professionals': '与专业人士直接安排细节',
    'Leave a review after your appointment': '预约后留下评价',
    'Loading reviews…': '正在加载评价…',
    'Loading professionals…': '正在加载专业人士…',
    'Loading dashboard…': '正在加载控制台…',
    'Loading requests…': '正在加载请求…',
    'Loading appointments…': '正在加载预约…',
  },
  vi: {
    'Review your submitted request details and response counts.': 'Xem chi tiết yêu cầu đã gửi và số lượng phản hồi.',
    'Response summary': 'Tóm tắt phản hồi',
    'Totals across your current and past requests.': 'Tổng hợp cho các yêu cầu hiện tại và trước đây.',
    'Active requests with open responses.': 'Yêu cầu đang hoạt động và còn phản hồi mở.',
    'Completed, expired, cancelled, and closed requests.': 'Yêu cầu đã hoàn tất, hết hạn, đã hủy hoặc đã đóng.',
    'Accepted professionals': 'Chuyên gia đã chấp nhận',
    'Open chat and continue your request.': 'Mở chat để tiếp tục yêu cầu của bạn.',
    'No accepted professionals yet.': 'Chưa có chuyên gia nào chấp nhận.',
    'Manage bookings, notes, payments, and message clients from one place.': 'Quản lý lịch hẹn, ghi chú, thanh toán và nhắn tin với khách hàng tại một nơi.',
    'No clients yet.': 'Chưa có khách hàng.',
    'Once you receive a request, clients will appear here.': 'Khi bạn nhận yêu cầu, khách hàng sẽ hiển thị tại đây.',
    'Ready to notify the 3 most suitable professionals?': 'Sẵn sàng gửi thông báo cho 3 chuyên gia phù hợp nhất?',
    'We send your request to the top matches only. All conversations stay in EUCA Match chat after login.':
      'Chúng tôi chỉ gửi yêu cầu của bạn đến các chuyên gia phù hợp nhất. Mọi cuộc trò chuyện sẽ ở trong chat EUCA Match sau khi đăng nhập.',
    'Top matches from verified professionals.': 'Gợi ý phù hợp nhất từ các chuyên gia đã xác minh.',
    'No professionals found yet in this category.': 'Chưa tìm thấy chuyên gia trong danh mục này.',
    'No professionals match your filters.': 'Không có chuyên gia phù hợp với bộ lọc của bạn.',
    'Identity & Compliance': 'Danh tính & tuân thủ',
    'Documents & Verification': 'Hồ sơ & xác minh',
    'Identity verification (choose ONE option)': 'Xác minh danh tính (chọn MỘT phương án)',
    'Use the same email and phone for clients': 'Dùng cùng email và số điện thoại cho khách hàng',
    'Finish your registration to start receiving client requests.': 'Hoàn tất đăng ký để bắt đầu nhận yêu cầu từ khách hàng.',
    'Legal, Governance & Compliance': 'Pháp lý, quản trị & tuân thủ',
    'Financial Advisory': 'Tư vấn tài chính',
    'Migration & Visa': 'Di trú & visa',
    'Education & Tutoring': 'Giáo dục & gia sư',
    'Property, Design & Building': 'Bất động sản, thiết kế & xây dựng',
    'Tech, Creative & Media': 'Công nghệ, sáng tạo & truyền thông',
    'Medical Doctors': 'Bác sĩ y khoa',
    'Allied Health': 'Y tế liên ngành',
    'Client-facing contact': 'Thông tin liên hệ hiển thị cho khách hàng',
    'Profile & pricing': 'Hồ sơ & mức phí',
    'Pricing notes (optional)': 'Ghi chú về giá (không bắt buộc)',
    'Open Client Dashboard': 'Mở bảng điều khiển khách hàng',
    'Open dashboard': 'Mở bảng điều khiển',
    'Message client': 'Nhắn tin cho khách hàng',
    'Message unavailable': 'Tạm thời chưa thể nhắn tin',
    'No pending requests.': 'Không có yêu cầu đang chờ.',
    'Try another filter or check back soon.': 'Hãy thử bộ lọc khác hoặc quay lại sau.',
    'No requests yet.': 'Chưa có yêu cầu.',
    'No appointments.': 'Chưa có lịch hẹn.',
    'No upcoming appointments yet.': 'Chưa có lịch hẹn sắp tới.',
    'No upcoming slots.': 'Chưa có khung giờ sắp tới.',
    'Secure payment': 'Thanh toán an toàn',
    'Review selection': 'Xem lại lựa chọn',
    'Pick a slot': 'Chọn khung giờ',
    'Ready to contact professionals?': 'Bạn đã sẵn sàng liên hệ chuyên gia chưa?',
    'Refine your search': 'Tinh chỉnh tìm kiếm',
    'Primary recommendation': 'Gợi ý chính',
    'You may also need': 'Bạn có thể cũng cần',
    'If relevant, explore this category too.': 'Nếu phù hợp, bạn có thể xem thêm danh mục này.',
    'AI Search': 'Tìm kiếm AI',
    'What do you need help with?': 'Bạn cần hỗ trợ điều gì?',
    'Search suggestions': 'Gợi ý tìm kiếm',
    'See more professionals': 'Xem thêm chuyên gia',
    'Arrange details directly with professionals': 'Trao đổi chi tiết trực tiếp với chuyên gia',
    'Leave a review after your appointment': 'Để lại đánh giá sau buổi hẹn',
    'Loading reviews…': 'Đang tải đánh giá…',
    'Loading professionals…': 'Đang tải chuyên gia…',
    'Loading dashboard…': 'Đang tải bảng điều khiển…',
    'Loading requests…': 'Đang tải yêu cầu…',
    'Loading appointments…': 'Đang tải lịch hẹn…',
  },
  ja: {
    'Review your submitted request details and response counts.': '送信した依頼内容と返信件数を確認できます。',
    'Response summary': '返信サマリー',
    'Totals across your current and past requests.': '現在と過去の依頼の合計です。',
    'Active requests with open responses.': '返信受付中の進行中依頼です。',
    'Completed, expired, cancelled, and closed requests.': '完了・期限切れ・キャンセル・終了した依頼です。',
    'Accepted professionals': '承諾した専門家',
    'Open chat and continue your request.': 'チャットを開いて依頼を進めてください。',
    'No accepted professionals yet.': 'まだ承諾した専門家はいません。',
    'Manage bookings, notes, payments, and message clients from one place.': '予約、ノート、支払い、クライアント連絡を1か所で管理できます。',
    'No clients yet.': 'まだクライアントはいません。',
    'Once you receive a request, clients will appear here.': '依頼を受けると、ここにクライアントが表示されます。',
    'Ready to notify the 3 most suitable professionals?': '最適な3名の専門家に通知しますか？',
    'We send your request to the top matches only. All conversations stay in EUCA Match chat after login.':
      '依頼は最適な候補のみに送信されます。ログイン後のやり取りはすべて EUCA Match チャット内で行われます。',
    'Top matches from verified professionals.': '認証済み専門家からの最適候補です。',
    'No professionals found yet in this category.': 'このカテゴリにはまだ専門家が見つかりません。',
    'No professionals match your filters.': '条件に一致する専門家がいません。',
    'Identity & Compliance': '本人確認とコンプライアンス',
    'Documents & Verification': '書類と認証',
    'Identity verification (choose ONE option)': '本人確認（いずれか1つを選択）',
    'Use the same email and phone for clients': 'クライアント向けに同じメールと電話番号を使用する',
    'Finish your registration to start receiving client requests.': '登録を完了すると、クライアント依頼の受信を開始できます。',
    'Legal, Governance & Compliance': '法務・ガバナンス・コンプライアンス',
    'Financial Advisory': 'ファイナンシャルアドバイザリー',
    'Migration & Visa': '移民・ビザ',
    'Education & Tutoring': '教育・個別指導',
    'Property, Design & Building': '不動産・設計・建築',
    'Tech, Creative & Media': 'テック・クリエイティブ・メディア',
    'Medical Doctors': '医師',
    'Allied Health': 'コメディカル',
    'Client-facing contact': 'クライアント公開連絡先',
    'Profile & pricing': 'プロフィールと料金',
    'Pricing notes (optional)': '料金メモ（任意）',
    'Open Client Dashboard': 'クライアントダッシュボードを開く',
    'Open dashboard': 'ダッシュボードを開く',
    'Message client': 'クライアントに連絡',
    'Message unavailable': 'メッセージ不可',
    'No pending requests.': '保留中の依頼はありません。',
    'Try another filter or check back soon.': '別の条件を試すか、しばらくしてから再確認してください。',
    'No requests yet.': '依頼はまだありません。',
    'No appointments.': '予約はありません。',
    'No upcoming appointments yet.': '今後の予約はまだありません。',
    'No upcoming slots.': '今後の空き枠はありません。',
    'Secure payment': '安全な支払い',
    'Review selection': '選択内容の確認',
    'Pick a slot': '時間枠を選択',
    'Ready to contact professionals?': '専門家に連絡する準備はできましたか？',
    'Refine your search': '検索条件を調整',
    'Primary recommendation': 'おすすめ候補',
    'You may also need': 'こちらもおすすめ',
    'If relevant, explore this category too.': '必要に応じて、このカテゴリもご確認ください。',
    'AI Search': 'AI検索',
    'What do you need help with?': 'どのようなサポートが必要ですか？',
    'Search suggestions': '検索候補',
    'See more professionals': '専門家をさらに見る',
    'Arrange details directly with professionals': '専門家と直接詳細を調整',
    'Leave a review after your appointment': '予約後にレビューを投稿',
    'Loading reviews…': 'レビューを読み込み中…',
    'Loading professionals…': '専門家を読み込み中…',
    'Loading dashboard…': 'ダッシュボードを読み込み中…',
    'Loading requests…': '依頼を読み込み中…',
    'Loading appointments…': '予約を読み込み中…',
  },
  ko: {
    'Review your submitted request details and response counts.': '제출한 요청 상세와 응답 수를 확인하세요.',
    'Response summary': '응답 요약',
    'Totals across your current and past requests.': '현재 및 과거 요청의 전체 합계입니다.',
    'Active requests with open responses.': '응답이 열려 있는 진행 중 요청입니다.',
    'Completed, expired, cancelled, and closed requests.': '완료, 만료, 취소, 종료된 요청입니다.',
    'Accepted professionals': '수락한 전문가',
    'Open chat and continue your request.': '채팅을 열고 요청을 이어가세요.',
    'No accepted professionals yet.': '아직 수락한 전문가가 없습니다.',
    'Manage bookings, notes, payments, and message clients from one place.': '한 곳에서 예약, 노트, 결제, 고객 메시지를 관리하세요.',
    'No clients yet.': '아직 고객이 없습니다.',
    'Once you receive a request, clients will appear here.': '요청을 받으면 고객이 여기에 표시됩니다.',
    'Ready to notify the 3 most suitable professionals?': '가장 적합한 전문가 3명에게 알림을 보낼까요?',
    'We send your request to the top matches only. All conversations stay in EUCA Match chat after login.':
      '요청은 상위 매칭 전문가에게만 전달됩니다. 로그인 후 모든 대화는 EUCA Match 채팅에서 진행됩니다.',
    'Top matches from verified professionals.': '검증된 전문가의 상위 매칭 결과입니다.',
    'No professionals found yet in this category.': '이 카테고리에는 아직 전문가가 없습니다.',
    'No professionals match your filters.': '필터와 일치하는 전문가가 없습니다.',
    'Identity & Compliance': '신원 및 컴플라이언스',
    'Documents & Verification': '문서 및 인증',
    'Identity verification (choose ONE option)': '신원 인증(한 가지 옵션 선택)',
    'Use the same email and phone for clients': '고객용 이메일과 전화번호를 동일하게 사용',
    'Finish your registration to start receiving client requests.': '등록을 완료하면 고객 요청을 받을 수 있습니다.',
    'Legal, Governance & Compliance': '법률, 거버넌스 및 컴플라이언스',
    'Financial Advisory': '재무 자문',
    'Migration & Visa': '이민 및 비자',
    'Education & Tutoring': '교육 및 튜터링',
    'Property, Design & Building': '부동산, 디자인 및 건축',
    'Tech, Creative & Media': '기술, 크리에이티브 및 미디어',
    'Medical Doctors': '의사',
    'Allied Health': '보건의료',
    'Client-facing contact': '고객 공개 연락처',
    'Profile & pricing': '프로필 및 요금',
    'Pricing notes (optional)': '요금 메모(선택)',
    'Open Client Dashboard': '고객 대시보드 열기',
    'Open dashboard': '대시보드 열기',
    'Message client': '고객에게 메시지',
    'Message unavailable': '메시지 사용 불가',
    'No pending requests.': '대기 중 요청이 없습니다.',
    'Try another filter or check back soon.': '다른 필터를 시도하거나 잠시 후 다시 확인하세요.',
    'No requests yet.': '아직 요청이 없습니다.',
    'No appointments.': '예약이 없습니다.',
    'No upcoming appointments yet.': '예정된 예약이 아직 없습니다.',
    'No upcoming slots.': '예정된 슬롯이 없습니다.',
    'Secure payment': '안전 결제',
    'Review selection': '선택 내용 확인',
    'Pick a slot': '시간 선택',
    'Ready to contact professionals?': '전문가에게 연락할 준비가 되셨나요?',
    'Refine your search': '검색 세부 조정',
    'Primary recommendation': '주요 추천',
    'You may also need': '함께 고려해보세요',
    'If relevant, explore this category too.': '필요하면 이 카테고리도 확인해 보세요.',
    'AI Search': 'AI 검색',
    'What do you need help with?': '어떤 도움이 필요하신가요?',
    'Search suggestions': '검색 제안',
    'See more professionals': '전문가 더 보기',
    'Arrange details directly with professionals': '전문가와 직접 세부사항 조율',
    'Leave a review after your appointment': '예약 후 리뷰를 남겨주세요',
    'Loading reviews…': '리뷰 불러오는 중…',
    'Loading professionals…': '전문가 불러오는 중…',
    'Loading dashboard…': '대시보드 불러오는 중…',
    'Loading requests…': '요청 불러오는 중…',
    'Loading appointments…': '예약 불러오는 중…',
  },
  fr: {
    'Review your submitted request details and response counts.': 'Consultez le détail de vos demandes envoyées et le nombre de réponses.',
    'Response summary': 'Résumé des réponses',
    'Totals across your current and past requests.': 'Totaux de vos demandes actuelles et passées.',
    'Active requests with open responses.': 'Demandes actives avec réponses en cours.',
    'Completed, expired, cancelled, and closed requests.': 'Demandes terminées, expirées, annulées ou clôturées.',
    'Accepted professionals': 'Professionnels acceptés',
    'Open chat and continue your request.': 'Ouvrez le chat pour poursuivre votre demande.',
    'No accepted professionals yet.': 'Aucun professionnel n’a encore accepté.',
    'Manage bookings, notes, payments, and message clients from one place.':
      'Gérez vos réservations, notes, paiements et messages clients depuis un seul espace.',
    'No clients yet.': 'Aucun client pour le moment.',
    'Once you receive a request, clients will appear here.': 'Dès qu’une demande arrive, les clients apparaîtront ici.',
    'Ready to notify the 3 most suitable professionals?': 'Prêt à notifier les 3 professionnels les plus adaptés ?',
    'We send your request to the top matches only. All conversations stay in EUCA Match chat after login.':
      'Nous envoyons votre demande uniquement aux meilleurs profils. Toutes les conversations restent dans le chat EUCA Match après connexion.',
    'Top matches from verified professionals.': 'Meilleures correspondances parmi les professionnels vérifiés.',
    'No professionals found yet in this category.': 'Aucun professionnel trouvé dans cette catégorie pour le moment.',
    'No professionals match your filters.': 'Aucun professionnel ne correspond à vos filtres.',
    'Identity & Compliance': 'Identité et conformité',
    'Documents & Verification': 'Documents et vérification',
    'Identity verification (choose ONE option)': 'Vérification d’identité (choisir UNE option)',
    'Use the same email and phone for clients': 'Utiliser le même e-mail et téléphone pour les clients',
    'Finish your registration to start receiving client requests.': 'Terminez votre inscription pour commencer à recevoir des demandes clients.',
    'Legal, Governance & Compliance': 'Juridique, gouvernance et conformité',
    'Financial Advisory': 'Conseil financier',
    'Migration & Visa': 'Migration et visa',
    'Education & Tutoring': 'Éducation et tutorat',
    'Property, Design & Building': 'Immobilier, design et construction',
    'Tech, Creative & Media': 'Tech, création et médias',
    'Medical Doctors': 'Médecins',
    'Allied Health': 'Santé paramédicale',
    'Client-facing contact': 'Contact visible par les clients',
    'Profile & pricing': 'Profil et tarification',
    'Pricing notes (optional)': 'Notes tarifaires (optionnel)',
    'Open Client Dashboard': 'Ouvrir le tableau de bord client',
    'Open dashboard': 'Ouvrir le tableau de bord',
    'Message client': 'Message au client',
    'Message unavailable': 'Messagerie indisponible',
    'No pending requests.': 'Aucune demande en attente.',
    'Try another filter or check back soon.': 'Essayez un autre filtre ou revenez bientôt.',
    'No requests yet.': 'Aucune demande pour le moment.',
    'No appointments.': 'Aucun rendez-vous.',
    'No upcoming appointments yet.': 'Aucun rendez-vous à venir pour le moment.',
    'No upcoming slots.': 'Aucun créneau à venir.',
    'Secure payment': 'Paiement sécurisé',
    'Review selection': 'Vérifier la sélection',
    'Pick a slot': 'Choisir un créneau',
    'Ready to contact professionals?': 'Prêt à contacter des professionnels ?',
    'Refine your search': 'Affinez votre recherche',
    'Primary recommendation': 'Recommandation principale',
    'You may also need': 'Vous pourriez aussi avoir besoin de',
    'If relevant, explore this category too.': 'Si pertinent, explorez aussi cette catégorie.',
    'AI Search': 'Recherche IA',
    'What do you need help with?': 'De quoi avez-vous besoin ?',
    'Search suggestions': 'Suggestions de recherche',
    'See more professionals': 'Voir plus de professionnels',
    'Arrange details directly with professionals': 'Organisez les détails directement avec les professionnels',
    'Leave a review after your appointment': 'Laissez un avis après votre rendez-vous',
    'Loading reviews…': 'Chargement des avis…',
    'Loading professionals…': 'Chargement des professionnels…',
    'Loading dashboard…': 'Chargement du tableau de bord…',
    'Loading requests…': 'Chargement des demandes…',
    'Loading appointments…': 'Chargement des rendez-vous…',
  },
  es: {
    'Review your submitted request details and response counts.': 'Revisa los detalles de tus solicitudes enviadas y el número de respuestas.',
    'Response summary': 'Resumen de respuestas',
    'Totals across your current and past requests.': 'Totales de tus solicitudes actuales y pasadas.',
    'Active requests with open responses.': 'Solicitudes activas con respuestas abiertas.',
    'Completed, expired, cancelled, and closed requests.': 'Solicitudes completadas, vencidas, canceladas y cerradas.',
    'Accepted professionals': 'Profesionales aceptados',
    'Open chat and continue your request.': 'Abre el chat y continúa tu solicitud.',
    'No accepted professionals yet.': 'Aún no hay profesionales aceptados.',
    'Manage bookings, notes, payments, and message clients from one place.':
      'Gestiona reservas, notas, pagos y mensajes con clientes desde un solo lugar.',
    'No clients yet.': 'Aún no hay clientes.',
    'Once you receive a request, clients will appear here.': 'Cuando recibas una solicitud, los clientes aparecerán aquí.',
    'Ready to notify the 3 most suitable professionals?': '¿Listo para notificar a los 3 profesionales más adecuados?',
    'We send your request to the top matches only. All conversations stay in EUCA Match chat after login.':
      'Enviamos tu solicitud solo a los perfiles más adecuados. Todas las conversaciones permanecen en el chat de EUCA Match después de iniciar sesión.',
    'Top matches from verified professionals.': 'Mejores coincidencias de profesionales verificados.',
    'No professionals found yet in this category.': 'Aún no se encontraron profesionales en esta categoría.',
    'No professionals match your filters.': 'Ningún profesional coincide con tus filtros.',
    'Identity & Compliance': 'Identidad y cumplimiento',
    'Documents & Verification': 'Documentos y verificación',
    'Identity verification (choose ONE option)': 'Verificación de identidad (elige UNA opción)',
    'Use the same email and phone for clients': 'Usar el mismo correo y teléfono para clientes',
    'Finish your registration to start receiving client requests.': 'Termina tu registro para empezar a recibir solicitudes de clientes.',
    'Legal, Governance & Compliance': 'Legal, gobernanza y cumplimiento',
    'Financial Advisory': 'Asesoría financiera',
    'Migration & Visa': 'Migración y visa',
    'Education & Tutoring': 'Educación y tutoría',
    'Property, Design & Building': 'Propiedad, diseño y construcción',
    'Tech, Creative & Media': 'Tecnología, creatividad y medios',
    'Medical Doctors': 'Médicos',
    'Allied Health': 'Salud aliada',
    'Client-facing contact': 'Contacto visible para clientes',
    'Profile & pricing': 'Perfil y precios',
    'Pricing notes (optional)': 'Notas de precios (opcional)',
    'Open Client Dashboard': 'Abrir panel del cliente',
    'Open dashboard': 'Abrir panel',
    'Message client': 'Mensaje al cliente',
    'Message unavailable': 'Mensajería no disponible',
    'No pending requests.': 'No hay solicitudes pendientes.',
    'Try another filter or check back soon.': 'Prueba otro filtro o vuelve a revisar pronto.',
    'No requests yet.': 'Aún no hay solicitudes.',
    'No appointments.': 'No hay citas.',
    'No upcoming appointments yet.': 'Aún no hay citas próximas.',
    'No upcoming slots.': 'No hay horarios próximos.',
    'Secure payment': 'Pago seguro',
    'Review selection': 'Revisar selección',
    'Pick a slot': 'Elegir horario',
    'Ready to contact professionals?': '¿Listo para contactar profesionales?',
    'Refine your search': 'Refina tu búsqueda',
    'Primary recommendation': 'Recomendación principal',
    'You may also need': 'También podrías necesitar',
    'If relevant, explore this category too.': 'Si aplica, explora también esta categoría.',
    'AI Search': 'Búsqueda con IA',
    'What do you need help with?': '¿Con qué necesitas ayuda?',
    'Search suggestions': 'Sugerencias de búsqueda',
    'See more professionals': 'Ver más profesionales',
    'Arrange details directly with professionals': 'Coordina detalles directamente con los profesionales',
    'Leave a review after your appointment': 'Deja una reseña después de tu cita',
    'Loading reviews…': 'Cargando reseñas…',
    'Loading professionals…': 'Cargando profesionales…',
    'Loading dashboard…': 'Cargando panel…',
    'Loading requests…': 'Cargando solicitudes…',
    'Loading appointments…': 'Cargando citas…',
  },
}

const PROFILE_NAV_TRANSLATIONS: Record<NonEnglishLanguage, Record<string, string>> = {
  zh: {
    Products: '产品',
    Resources: '资源',
    Company: '公司',
    'Knowledge base': '知识库',
    'Knowledge Base': '知识库',
    'Help Centre': '帮助中心',
    'Help Center': '帮助中心',
    'Help centre': '帮助中心',
    'Help center': '帮助中心',
    Contact: '联系',
    Categories: '分类',
    Category: '类别',
    'For clients': '面向客户',
    'For professionals': '面向专业人士',
    Pricing: '价格',
    Financial: '财务',
    'Financial Services': '金融服务',
    'Financial Advice': '财务建议',
    'Legal, Governance, and Compliance': '法律、治理与合规',
    'Property, Design and Building': '房产、设计与建筑',
    'Medical Doctors & Allied Health': '医疗医生与联合健康',
    'Medical Doctors and Allied Health': '医疗医生与联合健康',
  },
  vi: {
    Products: 'Sản phẩm',
    Resources: 'Tài nguyên',
    Company: 'Công ty',
    'Knowledge base': 'Kho kiến thức',
    'Knowledge Base': 'Kho kiến thức',
    'Help Centre': 'Trung tâm trợ giúp',
    'Help Center': 'Trung tâm trợ giúp',
    'Help centre': 'Trung tâm trợ giúp',
    'Help center': 'Trung tâm trợ giúp',
    Contact: 'Liên hệ',
    Categories: 'Danh mục',
    Category: 'Danh mục',
    'For clients': 'Dành cho khách hàng',
    'For professionals': 'Dành cho chuyên gia',
    Pricing: 'Giá',
    Financial: 'Tài chính',
    'Financial Services': 'Dịch vụ tài chính',
    'Financial Advice': 'Tư vấn tài chính',
    'Legal, Governance, and Compliance': 'Pháp lý, quản trị và tuân thủ',
    'Property, Design and Building': 'Bất động sản, thiết kế và xây dựng',
    'Medical Doctors & Allied Health': 'Bác sĩ y khoa và y tế liên ngành',
    'Medical Doctors and Allied Health': 'Bác sĩ y khoa và y tế liên ngành',
  },
  ja: {
    Products: '製品',
    Resources: 'リソース',
    Company: '会社',
    'Knowledge base': 'ナレッジベース',
    'Knowledge Base': 'ナレッジベース',
    'Help Centre': 'ヘルプセンター',
    'Help Center': 'ヘルプセンター',
    'Help centre': 'ヘルプセンター',
    'Help center': 'ヘルプセンター',
    Contact: 'お問い合わせ',
    Categories: 'カテゴリ',
    Category: 'カテゴリ',
    'For clients': 'クライアント向け',
    'For professionals': '専門家向け',
    Pricing: '料金',
    Financial: '金融',
    'Financial Services': '金融サービス',
    'Financial Advice': '財務アドバイス',
    'Legal, Governance, and Compliance': '法務・ガバナンス・コンプライアンス',
    'Property, Design and Building': '不動産・設計・建築',
    'Medical Doctors & Allied Health': '医師・コメディカル',
    'Medical Doctors and Allied Health': '医師・コメディカル',
  },
  ko: {
    Products: '제품',
    Resources: '리소스',
    Company: '회사',
    'Knowledge base': '지식베이스',
    'Knowledge Base': '지식베이스',
    'Help Centre': '고객지원 센터',
    'Help Center': '고객지원 센터',
    'Help centre': '고객지원 센터',
    'Help center': '고객지원 센터',
    Contact: '문의',
    Categories: '카테고리',
    Category: '카테고리',
    'For clients': '고객용',
    'For professionals': '전문가용',
    Pricing: '요금',
    Financial: '재무',
    'Financial Services': '금융 서비스',
    'Financial Advice': '재무 상담',
    'Legal, Governance, and Compliance': '법률, 거버넌스 및 컴플라이언스',
    'Property, Design and Building': '부동산, 디자인 및 건축',
    'Medical Doctors & Allied Health': '의사 및 보건의료',
    'Medical Doctors and Allied Health': '의사 및 보건의료',
  },
  fr: {
    Products: 'Produits',
    Resources: 'Ressources',
    Company: 'Entreprise',
    'Knowledge base': 'Base de connaissances',
    'Knowledge Base': 'Base de connaissances',
    'Help Centre': "Centre d'aide",
    'Help Center': "Centre d'aide",
    'Help centre': "Centre d'aide",
    'Help center': "Centre d'aide",
    Contact: 'Contact',
    Categories: 'Catégories',
    Category: 'Catégorie',
    'For clients': 'Pour les clients',
    'For professionals': 'Pour les professionnels',
    Pricing: 'Tarifs',
    Financial: 'Finance',
    'Financial Services': 'Services financiers',
    'Financial Advice': 'Conseil financier',
    'Legal, Governance, and Compliance': 'Juridique, gouvernance et conformité',
    'Property, Design and Building': 'Immobilier, design et construction',
    'Medical Doctors & Allied Health': 'Médecins et santé paramédicale',
    'Medical Doctors and Allied Health': 'Médecins et santé paramédicale',
  },
  es: {
    Products: 'Productos',
    Resources: 'Recursos',
    Company: 'Empresa',
    'Knowledge base': 'Base de conocimiento',
    'Knowledge Base': 'Base de conocimiento',
    'Help Centre': 'Centro de ayuda',
    'Help Center': 'Centro de ayuda',
    'Help centre': 'Centro de ayuda',
    'Help center': 'Centro de ayuda',
    Contact: 'Contacto',
    Categories: 'Categorías',
    Category: 'Categoría',
    'For clients': 'Para clientes',
    'For professionals': 'Para profesionales',
    Pricing: 'Precios',
    Financial: 'Finanzas',
    'Financial Services': 'Servicios financieros',
    'Financial Advice': 'Asesoría financiera',
    'Legal, Governance, and Compliance': 'Legal, gobernanza y cumplimiento',
    'Property, Design and Building': 'Propiedad, diseño y construcción',
    'Medical Doctors & Allied Health': 'Médicos y salud aliada',
    'Medical Doctors and Allied Health': 'Médicos y salud aliada',
  },
}

const EXTRA_TOKEN_TRANSLATIONS: Record<NonEnglishLanguage, Record<string, string>> = {
  zh: {
    Open: '打开',
    Close: '关闭',
    Details: '详情',
    Review: '评价',
    Reviews: '评价',
    Rating: '评分',
    Booking: '预订',
    Bookings: '预订',
    Available: '可用',
    Availability: '可用时间',
    Verified: '已认证',
    Verify: '认证',
    Contact: '联系',
    Submit: '提交',
    Sent: '已发送',
    Loading: '加载中',
    Failed: '失败',
    Error: '错误',
    Success: '成功',
    Current: '当前',
    Past: '历史',
    Summary: '概览',
    Totals: '总计',
    Awaiting: '等待',
    Response: '回复',
    Continue: '继续',
    Start: '开始',
    Cancel: '取消',
    Cancelled: '已取消',
    Confirmed: '已确认',
    Completed: '已完成',
    Rescheduled: '已改期',
    Expired: '已过期',
    Direct: '直接',
    Secure: '安全',
    Profile: '资料',
    Profiles: '资料',
    Identity: '身份',
    Compliance: '合规',
    Documents: '文件',
    Verification: '验证',
    Specialty: '专长',
    Specialties: '专长',
    Pricing: '价格',
    Country: '国家',
    Territory: '地区',
    City: '城市',
    State: '州',
    Province: '省',
    Address: '地址',
    Name: '姓名',
    First: '名',
    Last: '姓',
  },
  vi: {
    Open: 'Mở',
    Close: 'Đóng',
    Details: 'Chi tiết',
    Review: 'Đánh giá',
    Reviews: 'Đánh giá',
    Rating: 'Điểm',
    Booking: 'Đặt lịch',
    Bookings: 'Lịch đặt',
    Available: 'Sẵn có',
    Availability: 'Lịch trống',
    Verified: 'Đã xác minh',
    Verify: 'Xác minh',
    Contact: 'Liên hệ',
    Submit: 'Gửi',
    Sent: 'Đã gửi',
    Loading: 'Đang tải',
    Failed: 'Thất bại',
    Error: 'Lỗi',
    Success: 'Thành công',
    Current: 'Hiện tại',
    Past: 'Trước đây',
    Summary: 'Tóm tắt',
    Totals: 'Tổng',
    Awaiting: 'Đang chờ',
    Response: 'Phản hồi',
    Continue: 'Tiếp tục',
    Start: 'Bắt đầu',
    Cancel: 'Hủy',
    Cancelled: 'Đã hủy',
    Confirmed: 'Đã xác nhận',
    Completed: 'Hoàn tất',
    Rescheduled: 'Đổi lịch',
    Expired: 'Hết hạn',
    Direct: 'Trực tiếp',
    Secure: 'An toàn',
    Profile: 'Hồ sơ',
    Profiles: 'Hồ sơ',
    Identity: 'Danh tính',
    Compliance: 'Tuân thủ',
    Documents: 'Tài liệu',
    Verification: 'Xác minh',
    Specialty: 'Chuyên môn',
    Specialties: 'Chuyên môn',
    Pricing: 'Mức phí',
    Country: 'Quốc gia',
    Territory: 'Lãnh thổ',
    City: 'Thành phố',
    State: 'Bang',
    Province: 'Tỉnh',
    Address: 'Địa chỉ',
    Name: 'Tên',
    First: 'Tên',
    Last: 'Họ',
  },
  ja: {
    Open: '開く',
    Close: '閉じる',
    Details: '詳細',
    Review: 'レビュー',
    Reviews: 'レビュー',
    Rating: '評価',
    Booking: '予約',
    Bookings: '予約',
    Available: '利用可能',
    Availability: '空き状況',
    Verified: '認証済み',
    Verify: '認証',
    Contact: '連絡',
    Submit: '送信',
    Sent: '送信済み',
    Loading: '読み込み',
    Failed: '失敗',
    Error: 'エラー',
    Success: '成功',
    Current: '現在',
    Past: '過去',
    Summary: 'サマリー',
    Totals: '合計',
    Awaiting: '待機中',
    Response: '返信',
    Continue: '続行',
    Start: '開始',
    Cancel: 'キャンセル',
    Cancelled: 'キャンセル済み',
    Confirmed: '確定',
    Completed: '完了',
    Rescheduled: '日程変更',
    Expired: '期限切れ',
    Direct: '直接',
    Secure: '安全',
    Profile: 'プロフィール',
    Profiles: 'プロフィール',
    Identity: '本人確認',
    Compliance: 'コンプライアンス',
    Documents: '書類',
    Verification: '認証',
    Specialty: '専門分野',
    Specialties: '専門分野',
    Pricing: '料金',
    Country: '国',
    Territory: '地域',
    City: '市区町村',
    State: '州',
    Province: '県',
    Address: '住所',
    Name: '氏名',
    First: '名',
    Last: '姓',
  },
  ko: {
    Open: '열기',
    Close: '닫기',
    Details: '상세',
    Review: '리뷰',
    Reviews: '리뷰',
    Rating: '평점',
    Booking: '예약',
    Bookings: '예약',
    Available: '가능',
    Availability: '가능 시간',
    Verified: '검증됨',
    Verify: '검증',
    Contact: '연락',
    Submit: '제출',
    Sent: '전송됨',
    Loading: '불러오는 중',
    Failed: '실패',
    Error: '오류',
    Success: '성공',
    Current: '현재',
    Past: '이전',
    Summary: '요약',
    Totals: '합계',
    Awaiting: '대기',
    Response: '응답',
    Continue: '계속',
    Start: '시작',
    Cancel: '취소',
    Cancelled: '취소됨',
    Confirmed: '확정',
    Completed: '완료',
    Rescheduled: '재조정',
    Expired: '만료',
    Direct: '직접',
    Secure: '안전',
    Profile: '프로필',
    Profiles: '프로필',
    Identity: '신원',
    Compliance: '컴플라이언스',
    Documents: '문서',
    Verification: '인증',
    Specialty: '전문분야',
    Specialties: '전문분야',
    Pricing: '요금',
    Country: '국가',
    Territory: '지역',
    City: '도시',
    State: '주',
    Province: '도',
    Address: '주소',
    Name: '이름',
    First: '이름',
    Last: '성',
  },
  fr: {
    Open: 'Ouvrir',
    Close: 'Fermer',
    Details: 'Détails',
    Review: 'Avis',
    Reviews: 'Avis',
    Rating: 'Note',
    Booking: 'Réservation',
    Bookings: 'Réservations',
    Available: 'Disponible',
    Availability: 'Disponibilité',
    Verified: 'Vérifié',
    Verify: 'Vérifier',
    Contact: 'Contact',
    Submit: 'Envoyer',
    Sent: 'Envoyé',
    Loading: 'Chargement',
    Failed: 'Échec',
    Error: 'Erreur',
    Success: 'Succès',
    Current: 'Actuel',
    Past: 'Passé',
    Summary: 'Résumé',
    Totals: 'Totaux',
    Awaiting: 'En attente',
    Response: 'Réponse',
    Continue: 'Continuer',
    Start: 'Commencer',
    Cancel: 'Annuler',
    Cancelled: 'Annulé',
    Confirmed: 'Confirmé',
    Completed: 'Terminé',
    Rescheduled: 'Replanifié',
    Expired: 'Expiré',
    Direct: 'Direct',
    Secure: 'Sécurisé',
    Profile: 'Profil',
    Profiles: 'Profils',
    Identity: 'Identité',
    Compliance: 'Conformité',
    Documents: 'Documents',
    Verification: 'Vérification',
    Specialty: 'Spécialité',
    Specialties: 'Spécialités',
    Pricing: 'Tarification',
    Country: 'Pays',
    Territory: 'Territoire',
    City: 'Ville',
    State: 'État',
    Province: 'Province',
    Address: 'Adresse',
    Name: 'Nom',
    First: 'Prénom',
    Last: 'Nom',
  },
  es: {
    Open: 'Abrir',
    Close: 'Cerrar',
    Details: 'Detalles',
    Review: 'Reseña',
    Reviews: 'Reseñas',
    Rating: 'Calificación',
    Booking: 'Reserva',
    Bookings: 'Reservas',
    Available: 'Disponible',
    Availability: 'Disponibilidad',
    Verified: 'Verificado',
    Verify: 'Verificar',
    Contact: 'Contacto',
    Submit: 'Enviar',
    Sent: 'Enviado',
    Loading: 'Cargando',
    Failed: 'Falló',
    Error: 'Error',
    Success: 'Correcto',
    Current: 'Actual',
    Past: 'Anterior',
    Summary: 'Resumen',
    Totals: 'Totales',
    Awaiting: 'En espera',
    Response: 'Respuesta',
    Continue: 'Continuar',
    Start: 'Iniciar',
    Cancel: 'Cancelar',
    Cancelled: 'Cancelado',
    Confirmed: 'Confirmado',
    Completed: 'Completado',
    Rescheduled: 'Reprogramado',
    Expired: 'Vencido',
    Direct: 'Directo',
    Secure: 'Seguro',
    Profile: 'Perfil',
    Profiles: 'Perfiles',
    Identity: 'Identidad',
    Compliance: 'Cumplimiento',
    Documents: 'Documentos',
    Verification: 'Verificación',
    Specialty: 'Especialidad',
    Specialties: 'Especialidades',
    Pricing: 'Precios',
    Country: 'País',
    Territory: 'Territorio',
    City: 'Ciudad',
    State: 'Estado',
    Province: 'Provincia',
    Address: 'Dirección',
    Name: 'Nombre',
    First: 'Nombre',
    Last: 'Apellido',
  },
}

const PROTECTED_TERMS = [
  'Dr Dong Fang Zhao',
  'Dr Lily Vu',
  'EUCA Match',
  'EUCA MD®',
  'EUCA MD',
  'Calendly',
  'Melbourne',
  'Sydney',
  'Zoom',
  'Google',
  'Stripe',
  'AI',
].sort((a, b) => b.length - a.length)

const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim()
const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const ORIGINAL_TEXT_SYMBOL = Symbol('euca-original-text')

const buildProtectedMatcher = (term: string) => {
  const escaped = escapeRegex(term)
  const hasOnlyWordChars = /^[A-Za-z0-9]+$/.test(term)
  return hasOnlyWordChars ? new RegExp(`\\b${escaped}\\b`, 'gi') : new RegExp(escaped, 'gi')
}

const PROTECTED_TERM_MATCHERS = PROTECTED_TERMS.map(buildProtectedMatcher)
const PROTECTED_DYNAMIC_PATTERNS: RegExp[] = [
  /\b(?:[A-Z][A-Za-z&.'-]*\s+){0,5}(?:University|College|Institute|School|Academy)\b/g,
  /\b(?:[A-Z][A-Za-z&.'-]*\s+){0,5}(?:Award|Awards|Prize|Prizes|Medal|Medals|Fellowship|Distinction|Honour|Honor)\b/g,
  /\b(?:[A-Z][A-Za-z&.'-]*\s+){0,4}(?:Pty Ltd|Pty\. Ltd\.|Ltd|LLC|Inc|Incorporated|Corporation|Corp|Company|Co\.)\b/g,
]
const PROTECTED_TOKEN_PATTERN = /__EUCA(?:PROT|PROPER)_\d+_\d+__/g
const NAME_CLASS_HINT = /(provider|specialist|patient|client|reviewer|contact|profile|request-entry).*name|name-(line|btn|text|value)|reviews-provider-name|list-name-btn/i
const NON_NAME_UI_WORDS = new Set([
  'open', 'client', 'dashboard', 'request', 'requests', 'appointments', 'appointment', 'message', 'messages', 'notesai',
  'payment', 'payments', 'meeting', 'details', 'accepted', 'pending', 'awaiting', 'response', 'responses', 'review',
  'reviews', 'search', 'sort', 'account', 'clients', 'professionals', 'profile', 'profiles', 'secure', 'booking',
  'bookings', 'loading', 'new', 'most', 'recent', 'view', 'direct', 'verified', 'quick', 'identity', 'compliance',
  'resources', 'resource', 'company', 'product', 'products', 'knowledge', 'base', 'help', 'centre', 'center',
  'category', 'categories', 'pricing', 'services', 'service', 'financial', 'advisory', 'advice', 'legal', 'governance',
  'migration', 'visa', 'education', 'tutoring', 'property', 'design', 'building', 'allied', 'health', 'medical',
  'doctors', 'tech', 'creative', 'media',
])

const protectTerms = (value: string) => {
  let text = value
  const pairs: Array<{ token: string; value: string }> = []
  PROTECTED_TERM_MATCHERS.forEach((pattern, patternIndex) => {
    text = text.replace(pattern, (match) => {
      const token = `__EUCAPROT_${patternIndex}_${pairs.length}__`
      pairs.push({ token, value: match })
      return token
    })
  })
  PROTECTED_DYNAMIC_PATTERNS.forEach((pattern, patternIndex) => {
    text = text.replace(pattern, (match) => {
      const token = `__EUCAPROPER_${patternIndex}_${pairs.length}__`
      pairs.push({ token, value: match })
      return token
    })
  })
  return { text, pairs }
}

const restoreTerms = (value: string, pairs: Array<{ token: string; value: string }>) => {
  let next = value
  pairs.forEach(({ token, value: replacement }) => {
    next = next.split(token).join(replacement)
  })
  return next
}

const stripProtectedTokens = (value: string) => value.replace(PROTECTED_TOKEN_PATTERN, '')

const REMOTE_TRANSLATION_PRIMARY_TARGET: Record<NonEnglishLanguage, string> = {
  zh: 'zh',
  vi: 'vi',
  ja: 'ja',
  ko: 'ko',
  fr: 'fr',
  es: 'es',
}

const REMOTE_TRANSLATION_PRIMARY_ENDPOINT = 'https://translate.plausibility.cloud/api/v1'
const REMOTE_TRANSLATION_SECONDARY_ENDPOINT = 'https://api.mymemory.translated.net/get'
const REMOTE_TRANSLATION_SECONDARY_TARGET: Record<NonEnglishLanguage, string> = {
  zh: 'zh-CN',
  vi: 'vi',
  ja: 'ja',
  ko: 'ko',
  fr: 'fr',
  es: 'es',
}
const REMOTE_TRANSLATION_MAX_CHARS = 420
const REMOTE_TRANSLATION_CONCURRENCY = 8
const REMOTE_TRANSLATION_CACHE_STORAGE_KEY = 'euca.ui.remote.translation.cache.v1'
const REMOTE_TRANSLATION_CACHE_LIMIT = 6000

const remoteTranslationCache = new Map<string, string>()
const remoteTranslationPending = new Set<string>()
const remoteTranslationQueue: Array<{ language: NonEnglishLanguage; text: string }> = []
let remoteTranslationInFlight = 0
let remoteTranslationNotify: (() => void) | null = null
let remoteTranslationCacheHydrated = false

const remoteTranslationKey = (language: NonEnglishLanguage, text: string) => `${language}::${normalizeText(text)}`

const decodeHtmlEntities = (value: string) => {
  if (!value || typeof document === 'undefined') return value
  const textarea = document.createElement('textarea')
  textarea.innerHTML = value
  return textarea.value
}

const normalizeRemoteTranslationOutput = (value: unknown) => {
  const decoded = decodeHtmlEntities(String(value || '').trim())
  if (!decoded) return null
  const upper = decoded.toUpperCase()
  if (upper.includes('MYMEMORY WARNING')) return null
  if (upper.includes('YOU USED ALL AVAILABLE FREE TRANSLATIONS')) return null
  return decoded
}

const hydrateRemoteTranslationCache = () => {
  if (remoteTranslationCacheHydrated || typeof window === 'undefined') return
  remoteTranslationCacheHydrated = true
  try {
    const raw = window.localStorage.getItem(REMOTE_TRANSLATION_CACHE_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value !== 'string' || !key.includes('::')) return
      const source = key.split('::').slice(1).join('::')
      // Older builds cached untranslated English fallbacks; skip those so we can retry with live providers.
      if (normalizeText(source) && normalizeText(source) === normalizeText(value)) return
      remoteTranslationCache.set(key, value)
    })
  } catch {
    // ignore storage/cache parse errors
  }
}

let remoteTranslationPersistTimer: number | null = null
const persistRemoteTranslationCache = () => {
  if (typeof window === 'undefined') return
  if (remoteTranslationPersistTimer != null) window.clearTimeout(remoteTranslationPersistTimer)
  remoteTranslationPersistTimer = window.setTimeout(() => {
    remoteTranslationPersistTimer = null
    try {
      const entries = Array.from(remoteTranslationCache.entries()).slice(-REMOTE_TRANSLATION_CACHE_LIMIT)
      const payload: Record<string, string> = {}
      entries.forEach(([key, value]) => {
        payload[key] = value
      })
      window.localStorage.setItem(REMOTE_TRANSLATION_CACHE_STORAGE_KEY, JSON.stringify(payload))
    } catch {
      // ignore storage quota errors
    }
  }, 180)
}

const shouldAttemptRemoteTranslation = (value: string) => {
  const text = normalizeText(value)
  if (!text) return false
  if (text.length < 12 || text.length > REMOTE_TRANSLATION_MAX_CHARS) return false
  if (/https?:\/\/|www\.|@/.test(text)) return false
  if (!/[A-Za-z]/.test(stripProtectedTokens(text))) return false
  const words = text.split(/\s+/).filter((w) => /[A-Za-z]/.test(w))
  if (words.length < 3) return false
  const upperWords = words.filter((w) => w.length > 1 && w === w.toUpperCase()).length
  if (upperWords / Math.max(words.length, 1) > 0.7) return false
  return true
}

const englishWordCount = (value: string) => {
  const words = normalizeText(stripProtectedTokens(value)).match(/[A-Za-z][A-Za-z'-]*/g)
  return words ? words.length : 0
}

const shouldUseRemoteAfterTokenPass = (source: string, tokenTranslated: string) => {
  if (!shouldAttemptRemoteTranslation(source)) return false
  const sourceWords = englishWordCount(source)
  if (sourceWords < 3) return false
  const tokenWords = englishWordCount(tokenTranslated)
  // If token translation already removed most English words, skip remote for instant UI rendering.
  if (tokenWords <= 2) return false
  if (sourceWords >= 8 && tokenWords <= Math.ceil(sourceWords * 0.35)) return false
  return true
}

const getRemoteTranslation = (language: NonEnglishLanguage, text: string) => {
  hydrateRemoteTranslationCache()
  const key = remoteTranslationKey(language, text)
  const cached = remoteTranslationCache.get(key)
  return cached || null
}

const applyRemoteTranslationResult = (language: NonEnglishLanguage, text: string, translated: string | null) => {
  const key = remoteTranslationKey(language, text)
  const normalizedSource = normalizeText(text)
  const normalizedTranslated = normalizeText(translated || '')
  if (translated && normalizedTranslated && normalizedTranslated !== normalizedSource) {
    remoteTranslationCache.set(key, translated)
  } else {
    remoteTranslationCache.delete(key)
  }
  persistRemoteTranslationCache()
}

const fetchRemoteTranslationPrimary = async (language: NonEnglishLanguage, text: string): Promise<string | null> => {
  const target = REMOTE_TRANSLATION_PRIMARY_TARGET[language]
  if (!target) return null
  const encodedText = encodeURIComponent(text)
  const encodedTarget = encodeURIComponent(target)
  const url = `${REMOTE_TRANSLATION_PRIMARY_ENDPOINT}/en/${encodedTarget}/${encodedText}`
  try {
    const res = await fetch(url, { cache: 'no-store' })
    if (!res.ok) return null
    const json = await res.json().catch(() => ({}))
    return normalizeRemoteTranslationOutput((json as any)?.translation || (json as any)?.translatedText)
  } catch {
    return null
  }
}

const fetchRemoteTranslationSecondary = async (language: NonEnglishLanguage, text: string): Promise<string | null> => {
  const target = REMOTE_TRANSLATION_SECONDARY_TARGET[language]
  if (!target) return null
  try {
    const url = new URL(REMOTE_TRANSLATION_SECONDARY_ENDPOINT)
    url.searchParams.set('q', text)
    url.searchParams.set('langpair', `en|${target}`)
    const res = await fetch(url.toString(), { cache: 'no-store' })
    if (!res.ok) return null
    const json = await res.json().catch(() => ({}))
    const status = Number((json as any)?.responseStatus || 0)
    if (status >= 400) return null
    const raw = (json as any)?.responseData?.translatedText
    return normalizeRemoteTranslationOutput(raw)
  } catch {
    return null
  }
}

const fetchRemoteTranslation = async (language: NonEnglishLanguage, text: string): Promise<string | null> => {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return null
  const primary = await fetchRemoteTranslationPrimary(language, text)
  if (primary) return primary
  return await fetchRemoteTranslationSecondary(language, text)
}

const pumpRemoteTranslationQueue = () => {
  if (typeof window === 'undefined') return
  while (remoteTranslationInFlight < REMOTE_TRANSLATION_CONCURRENCY && remoteTranslationQueue.length) {
    const job = remoteTranslationQueue.shift()
    if (!job) break
    const key = remoteTranslationKey(job.language, job.text)
    remoteTranslationInFlight += 1
    void fetchRemoteTranslation(job.language, job.text)
      .then((translated) => applyRemoteTranslationResult(job.language, job.text, translated))
      .finally(() => {
        remoteTranslationPending.delete(key)
        remoteTranslationInFlight = Math.max(0, remoteTranslationInFlight - 1)
        remoteTranslationNotify?.()
        if (remoteTranslationQueue.length) pumpRemoteTranslationQueue()
      })
  }
}

const queueRemoteTranslation = (language: NonEnglishLanguage, text: string) => {
  hydrateRemoteTranslationCache()
  if (!shouldAttemptRemoteTranslation(text)) return
  const normalized = normalizeText(text)
  const key = remoteTranslationKey(language, normalized)
  if (remoteTranslationCache.has(key) || remoteTranslationPending.has(key)) return
  remoteTranslationPending.add(key)
  remoteTranslationQueue.push({ language, text: normalized })
  pumpRemoteTranslationQueue()
}

const splitRemoteSegments = (text: string) => {
  if (!text) return []
  if (text.length <= REMOTE_TRANSLATION_MAX_CHARS) return [text]

  const segments: string[] = []
  const sentenceUnits = text.match(/[^.!?\n]+[.!?]?\s*|\n+/g) || [text]
  let bucket = ''

  const pushBucket = () => {
    if (!bucket) return
    segments.push(bucket)
    bucket = ''
  }

  const pushLongUnit = (unit: string) => {
    const words = unit.match(/\S+\s*/g) || [unit]
    let local = ''
    words.forEach((word) => {
      if (!local) {
        local = word
        return
      }
      if ((local + word).length <= REMOTE_TRANSLATION_MAX_CHARS) {
        local += word
        return
      }
      segments.push(local)
      local = word
    })
    if (local) segments.push(local)
  }

  sentenceUnits.forEach((unit) => {
    if (unit.length > REMOTE_TRANSLATION_MAX_CHARS) {
      pushBucket()
      pushLongUnit(unit)
      return
    }
    if (!bucket) {
      bucket = unit
      return
    }
    if ((bucket + unit).length <= REMOTE_TRANSLATION_MAX_CHARS) {
      bucket += unit
      return
    }
    pushBucket()
    bucket = unit
  })
  pushBucket()
  return segments
}

const resolveRemoteTranslation = (language: NonEnglishLanguage, text: string) => {
  const parts = splitRemoteSegments(text)
  if (!parts.length) return null

  let changed = false
  const resolved = parts.map((part) => {
    const leading = part.match(/^\s*/)?.[0] ?? ''
    const trailing = part.match(/\s*$/)?.[0] ?? ''
    const core = part.slice(leading.length, part.length - trailing.length)
    if (!shouldAttemptRemoteTranslation(core)) return part

    const cached = getRemoteTranslation(language, core)
    if (cached) {
      if (cached !== core) changed = true
      return `${leading}${cached}${trailing}`
    }

    queueRemoteTranslation(language, core)
    return part
  })

  if (!changed) return null
  return resolved.join('')
}

const isLikelyPersonName = (value: string) => {
  const normalized = normalizeText(value)
  if (!normalized || normalized.length > 64) return false
  if (/[0-9]/.test(normalized)) return false
  if (/[,;:!?()[\]{}]/.test(normalized)) return false
  const tokens = normalized.split(/\s+/).filter(Boolean)
  if (tokens.length < 2 || tokens.length > 4) return false
  for (const token of tokens) {
    const word = token.replace(/^[^A-Za-z]+|[^A-Za-z'.-]+$/g, '')
    if (!word) return false
    if (NON_NAME_UI_WORDS.has(word.toLowerCase())) return false
    if (!/^[A-Z][A-Za-z'.-]{1,}$/.test(word) && !/^[A-Z]{2,}$/.test(word)) return false
  }
  return true
}

const shouldPreservePotentialName = (element: Element | null, value: string) => {
  const trimmed = normalizeText(value)
  if (!trimmed) return false
  if (PROTECTED_TERMS.some((term) => trimmed.toLowerCase() === term.toLowerCase())) return true
  const className = typeof (element as any)?.className === 'string' ? String((element as any).className) : ''
  const id = (element as HTMLElement | null)?.id || ''
  if (NAME_CLASS_HINT.test(`${className} ${id}`) && trimmed.length <= 80) return true
  return isLikelyPersonName(trimmed)
}

const getStoredLanguage = (): UiLanguageCode => {
  if (typeof window === 'undefined') return DEFAULT_UI_LANGUAGE
  try {
    const raw = window.localStorage.getItem(UI_LANGUAGE_STORAGE_KEY)
    if (raw && UI_LANGUAGE_OPTIONS.some((option) => option.code === raw)) return raw as UiLanguageCode
  } catch {
    // ignore storage errors
  }
  return DEFAULT_UI_LANGUAGE
}

const persistLanguage = (language: UiLanguageCode) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, language)
  } catch {
    // ignore storage errors
  }
}

const buildTokenMatchers = (language: NonEnglishLanguage) => {
  const table = {
    ...(TOKEN_TRANSLATIONS[language] || {}),
    ...(EXTRA_TOKEN_TRANSLATIONS[language] || {}),
  }
  const keys = Object.keys(table).sort((a, b) => b.length - a.length)
  return keys.map((key) => {
    const hasOnlyWordChars = /^[A-Za-z0-9_]+$/.test(key)
    const pattern = hasOnlyWordChars
      ? new RegExp(`\\b${escapeRegex(key)}\\b`, 'g')
      : new RegExp(escapeRegex(key), 'g')
    return { pattern, replacement: table[key] }
  })
}

const shouldSkipElement = (element: Element | null, mode: 'text' | 'attr' = 'text') => {
  if (!element) return true
  if (element.closest('[data-no-i18n], .no-i18n')) return true
  const tagName = element.tagName
  if (mode === 'text') {
    if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'SVG'].includes(tagName)) return true
  } else {
    if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'SVG'].includes(tagName)) return true
  }
  return false
}

const applyAttributeTranslation = (element: Element, attr: string, language: UiLanguageCode, translate: (text: string) => string) => {
  const current = element.getAttribute(attr)
  if (current == null) return
  const shadowAttr = `data-euca-i18n-orig-${attr.replace(/[^a-z0-9_-]/gi, '_')}`
  const original = element.getAttribute(shadowAttr) ?? current
  if (!element.hasAttribute(shadowAttr)) element.setAttribute(shadowAttr, original)
  const next = language === 'en' ? original : translate(original)
  if (next !== current) element.setAttribute(attr, next)
}

const translateTree = (language: UiLanguageCode, translate: (text: string) => string) => {
  const root = document.body
  if (!root) return

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let current = walker.nextNode() as Text | null
  while (current) {
    const parent = current.parentElement
    const raw = current.nodeValue ?? ''
    if (!shouldSkipElement(parent, 'text') && raw.trim()) {
      const holder = current as Text & { [ORIGINAL_TEXT_SYMBOL]?: string }
      const original = holder[ORIGINAL_TEXT_SYMBOL] ?? raw
      if (!holder[ORIGINAL_TEXT_SYMBOL]) holder[ORIGINAL_TEXT_SYMBOL] = original
      const next = language === 'en'
        ? original
        : (shouldPreservePotentialName(parent, original) ? original : translate(original))
      if (next !== raw) current.nodeValue = next
    }
    current = walker.nextNode() as Text | null
  }

  const elements = root.querySelectorAll('[placeholder], [title], [aria-label], input[type="submit"], input[type="button"], input[type="reset"]')
  elements.forEach((element) => {
    if (shouldSkipElement(element, 'attr')) return
    if (element.hasAttribute('placeholder')) applyAttributeTranslation(element, 'placeholder', language, translate)
    if (element.hasAttribute('title')) applyAttributeTranslation(element, 'title', language, translate)
    if (element.hasAttribute('aria-label')) applyAttributeTranslation(element, 'aria-label', language, translate)
    if (element instanceof HTMLInputElement) {
      const type = String(element.type || '').toLowerCase()
      if (['submit', 'button', 'reset'].includes(type)) {
        const shadowAttr = 'data-euca-i18n-orig-value'
        const original = element.getAttribute(shadowAttr) ?? element.value
        if (!element.hasAttribute(shadowAttr)) element.setAttribute(shadowAttr, original)
        const next = language === 'en' ? original : translate(original)
        if (next !== element.value) element.value = next
      }
    }
  })
}

type UiLanguageContextValue = {
  language: UiLanguageCode
  setLanguage: (language: UiLanguageCode) => void
}

const UiLanguageContext = createContext<UiLanguageContextValue | null>(null)

export const UiLanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<UiLanguageCode>(() => getStoredLanguage())

  const exactLookup = useMemo(() => {
    if (language === 'en') return new Map<string, string>()
    const table = {
      ...(EXPLICIT_TRANSLATIONS[language] || {}),
      ...(EXTRA_EXPLICIT_TRANSLATIONS[language] || {}),
      ...(PROFILE_NAV_TRANSLATIONS[language] || {}),
    }
    const entries = Object.entries(table).map(([key, value]) => [normalizeText(key), value] as const)
    return new Map<string, string>(entries)
  }, [language])

  const tokenMatchers = useMemo(
    () => (language === 'en' ? [] : buildTokenMatchers(language)),
    [language]
  )

  const translate = useMemo(
    () => {
      const memo = new Map<string, string>()
      return (input: string) => {
        if (language === 'en') return input
        const source = String(input ?? '')
        const memoHit = memo.get(source)
        if (memoHit != null) return memoHit
        const remoteLanguage = language as NonEnglishLanguage
        if (!source.trim()) {
          memo.set(source, source)
          return source
        }
        const leading = source.match(/^\s*/)?.[0] ?? ''
        const trailing = source.match(/\s*$/)?.[0] ?? ''
        const core = source.slice(leading.length, source.length - trailing.length)
        if (!/[A-Za-z]/.test(core)) {
          memo.set(source, source)
          return source
        }

        const exact = exactLookup.get(normalizeText(core))
        if (exact) {
          const out = `${leading}${exact}${trailing}`
          memo.set(source, out)
          return out
        }

        const { text: protectedCore, pairs } = protectTerms(core)
        if (!/[A-Za-z]/.test(stripProtectedTokens(protectedCore))) {
          const out = `${leading}${restoreTerms(protectedCore, pairs)}${trailing}`
          memo.set(source, out)
          return out
        }

        let next = protectedCore
        tokenMatchers.forEach(({ pattern, replacement }) => {
          next = next.replace(pattern, replacement)
        })
        if (shouldUseRemoteAfterTokenPass(protectedCore, next)) {
          const remote = resolveRemoteTranslation(remoteLanguage, protectedCore)
          if (remote) next = remote
        }
        const out = `${leading}${restoreTerms(next, pairs)}${trailing}`
        memo.set(source, out)
        return out
      }
    },
    [language, exactLookup, tokenMatchers]
  )

  useLayoutEffect(() => {
    document.documentElement.lang = HTML_LANG_BY_CODE[language] || 'en'
    translateTree(language, translate)

    let scheduled = false
    let applying = false
    const apply = () => {
      if (applying) return
      applying = true
      try {
        translateTree(language, translate)
      } finally {
        applying = false
      }
    }
    const schedule = () => {
      if (scheduled) return
      scheduled = true
      const enqueue =
        typeof queueMicrotask === 'function'
          ? queueMicrotask
          : (cb: () => void) => Promise.resolve().then(cb)
      enqueue(() => {
        scheduled = false
        apply()
      })
    }

    remoteTranslationNotify = schedule

    const observer = new MutationObserver((records) => {
      const shouldApply = records.some((record) => {
        if (record.type === 'characterData') return true
        if (record.type === 'childList') return record.addedNodes.length > 0 || record.removedNodes.length > 0
        if (record.type === 'attributes') {
          const attribute = String(record.attributeName || '')
          return ['placeholder', 'title', 'aria-label', 'value'].includes(attribute)
        }
        return false
      })
      if (!shouldApply) return
      schedule()
    })
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['placeholder', 'title', 'aria-label', 'value'],
      })
    }

    const settleTimeouts: number[] = []
    if (typeof window !== 'undefined') {
      ;[0, 60, 180, 360].forEach((delay) => {
        settleTimeouts.push(window.setTimeout(schedule, delay))
      })
    }

    let routePollId: number | null = null
    if (typeof window !== 'undefined') {
      let lastRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`
      routePollId = window.setInterval(() => {
        const nextRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`
        if (nextRoute === lastRoute) return
        lastRoute = nextRoute
        schedule()
        settleTimeouts.push(window.setTimeout(schedule, 80))
        settleTimeouts.push(window.setTimeout(schedule, 220))
      }, 140)
    }

    return () => {
      observer.disconnect()
      if (typeof window !== 'undefined') {
        settleTimeouts.forEach((id) => window.clearTimeout(id))
      }
      if (routePollId != null && typeof window !== 'undefined') {
        window.clearInterval(routePollId)
      }
      if (remoteTranslationNotify === schedule) remoteTranslationNotify = null
    }
  }, [language, translate])

  const setLanguage = (next: UiLanguageCode) => {
    if (!UI_LANGUAGE_OPTIONS.some((option) => option.code === next)) return
    setLanguageState(next)
    persistLanguage(next)
  }

  return (
    <UiLanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </UiLanguageContext.Provider>
  )
}

export const useUiLanguage = () => {
  const ctx = useContext(UiLanguageContext)
  if (!ctx) {
    throw new Error('useUiLanguage must be used within UiLanguageProvider')
  }
  return ctx
}
